import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, FlaskConical, Truck, Check } from 'lucide-react';

const QualitySection = () => {
  const { t, currentLanguage } = useLanguage();

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Decorative DNA pattern */}
      <div className="absolute right-0 top-0 h-full w-64 opacity-5">
        <svg viewBox="0 0 200 600" fill="none" className="h-full w-full">
          <path d="M50 0 Q120 75 50 150 Q-20 225 50 300 Q120 375 50 450 Q-20 525 50 600" stroke="#14B8A6" strokeWidth="3" fill="none"/>
          <path d="M150 0 Q80 75 150 150 Q220 225 150 300 Q80 375 150 450 Q220 525 150 600" stroke="#14B8A6" strokeWidth="3" fill="none"/>
        </svg>
      </div>
      
      {/* Floating spheres */}
      <div className="absolute top-20 right-20 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-teal/10 to-capsule-aqua/10 blur-xl"></div>
      <div className="absolute bottom-20 left-20 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-coral/10 to-capsule-salmon/10 blur-xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Image */}
          <div className="relative">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-capsule-teal/20 to-capsule-aqua/20 rounded-3xl transform rotate-3"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_f8e2aef4-451e-4438-8a32-262b0341d7ed/artifacts/jm94s9y2_WhatsApp%20Image%202026-02-07%20at%2001.04.11%20%281%29.jpeg"
                alt="PenLab Peptide Quality"
                className="w-full rounded-3xl shadow-2xl relative"
              />
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 glass-card rounded-2xl px-4 py-3 shadow-lg">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Purity</p>
                    <p className="font-bold text-capsule-teal">99%+</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 glass-card rounded-2xl px-4 py-3 shadow-lg">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-capsule-coral to-capsule-salmon flex items-center justify-center">
                    <FlaskConical className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">COA</p>
                    <p className="font-bold text-capsule-coral">{currentLanguage === 'es' ? 'Incluido' : currentLanguage === 'ru' ? 'Включен' : 'Included'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Side - Content */}
          <div className="space-y-8">
            <div>
              <span className="text-capsule-teal text-sm font-semibold tracking-wider">
                {currentLanguage === 'es' ? 'CALIDAD PREMIUM' : currentLanguage === 'ru' ? 'ПРЕМИУМ КАЧЕСТВО' : 'PREMIUM QUALITY'}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-2 mb-6 leading-tight">
                {t('quality_title')}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('quality_subtitle')}
              </p>
            </div>
            
            {/* Features */}
            <div className="space-y-5">
              <div className="flex items-start space-x-4 p-4 rounded-2xl bg-capsule-bg-light/50 hover:bg-capsule-bg-light transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-xl flex items-center justify-center shadow-teal-glow">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('quality_feature1_title')}</h3>
                  <p className="text-gray-600 text-sm">{t('quality_feature1_desc')}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 rounded-2xl bg-capsule-bg-light/50 hover:bg-capsule-bg-light transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('quality_feature2_title')}</h3>
                  <p className="text-gray-600 text-sm">{t('quality_feature2_desc')}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 rounded-2xl bg-capsule-bg-light/50 hover:bg-capsule-bg-light transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-capsule-coral to-capsule-salmon rounded-xl flex items-center justify-center shadow-coral-glow">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('quality_feature3_title')}</h3>
                  <p className="text-gray-600 text-sm">{t('quality_feature3_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QualitySection;
